import NextImage, { ImageLoader, ImageLoaderProps } from 'next/image';
import type { ComponentPropsWithoutRef } from 'react';
import {
  isContentfulAssetProxyUrl,
  isContentfulImageUrl,
} from '../../../utils/contentful';

type NextImageSrc = ComponentPropsWithoutRef<typeof NextImage>['src'];

const CONTENTFUL_LOADER_DEFAULT_QUALITY = 80;

export const getImageLoader = (src: NextImageSrc): ImageLoader | undefined => {
  if (typeof src !== 'string') {
    return undefined;
  }

  if (isContentfulImageUrl(src) || isContentfulAssetProxyUrl(src)) {
    return contentfulImageLoader;
  }

  return undefined;
};

export const contentfulImageLoader = ({
  src,
  width,
  quality,
}: ImageLoaderProps): string => {
  let url = new URL(src);

  // url.searchParams.set('fm', 'avif');
  url.searchParams.set(
    'q',
    (quality ?? CONTENTFUL_LOADER_DEFAULT_QUALITY).toString(),
  );

  let previousWidth = url.searchParams.get('w');
  let previousHeight = url.searchParams.get('h');
  let previousFit = url.searchParams.get('fit');

  let ratio = getAspectRatio(previousWidth, previousHeight);

  if (previousFit) {
    url.searchParams.set('fit', previousFit);
  }

  url.searchParams.set('w', width.toString());

  if (typeof ratio === 'undefined') {
    return url.href;
  }

  let height = Math.round(width / ratio);

  url.searchParams.set('h', height.toString());
  url.searchParams.set('fit', previousFit ?? 'fill');

  return url.href;
};

export const getAspectRatio = (
  width: string | null,
  height: string | null,
): number | undefined => {
  if (width === null || height === null) {
    return undefined;
  }

  let widthAsNumber = parseInt(width, 10);
  let heightAsNumber = parseInt(height, 10);

  return widthAsNumber / heightAsNumber;
};

export const changeImageFormatOrSource = (
  src: string,
  format: 'jpg' | 'png' | 'webp' | 'avif',
) => {
  if (isContentfulImageUrl(src) || isContentfulAssetProxyUrl(src)) {
    let url = new URL(src);
    url.searchParams.set('fm', format);
    return url.href;
  }
  return src;
};

export const changeImageQuality = (src: string, quality: number) => {
  if (isContentfulImageUrl(src) || isContentfulAssetProxyUrl(src)) {
    let url = new URL(src);
    url.searchParams.set('q', quality.toString());
    return url.href;
  }
  return src;
};

/**
 * Changes the resizing behavior of the image source.
 *
 * Note: This only works for images served by Contentful!
 *
 * @see https://www.contentful.com/developers/docs/references/images-api/#/reference/resizing-&-cropping/change-the-resizing-behavior
 * @param src The image source URL to modify.
 * @param fit The desired resizing behavior. Possible values can be found in the Contentful documentation.
 * @returns The modified image URL with the updated resizing behavior, or the original URL if it's not a Contentful image.
 */
export const changeResizingBehavior = (src: string, fit: string) => {
  if (isContentfulImageUrl(src) || isContentfulAssetProxyUrl(src)) {
    const url = new URL(src);
    url.searchParams.set('fit', fit);
    return url.href;
  }
  return src;
};
